import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Palette } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      navigate(user.is_admin ? '/admin' : '/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset-request`, { email: resetEmail });
      toast.success('If an account exists with this email, a reset link has been sent.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-artsy-background flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-teal-600 rounded-full border-2 border-slate-900 shadow-sticker flex items-center justify-center">
              <Palette className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold text-slate-900">
              Artsy <span className="text-pink-500">Craftsy</span>
            </span>
          </Link>
        </div>

        {/* Login Card */}
        <div className="card-pricing">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2 text-center">Welcome Back!</h1>
          <p className="font-body text-slate-600 text-center mb-8">Sign in to access your account</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-body font-semibold text-slate-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-artsy"
                placeholder="your@email.com"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block font-body font-semibold text-slate-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-artsy pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="font-body text-sm text-teal-600 hover:text-teal-700 hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="font-body text-slate-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-teal-600 font-semibold hover:underline" data-testid="goto-register">
                Register here
              </Link>
            </p>
          </div>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="card-plain max-w-md w-full">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Reset Password</h2>
              <p className="font-body text-slate-600 mb-4">Enter your email address and we'll send you a link to reset your password.</p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="input-artsy"
                    placeholder="your@email.com"
                    required
                    data-testid="reset-email-input"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                    className="btn-outline flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                    disabled={resetLoading}
                    data-testid="send-reset-link"
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
